// src/components/modals/PrazoSimulatorModal.js
import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Clock,
  Calendar,
  Search,
  Plus,
  Trash2,
  Copy,
  Check,
  Sparkles,
  Layers,
  ArrowRight,
  Share2,
  Info,
  CalendarDays,
  ShieldCheck,
  AlertCircle,
  Gem,
} from "lucide-react";
import { useProductionContext } from "../../contexts/ProductionContext";
import { useCatalog } from "../../hooks/useCatalog";
import { cleanSku, getModelFromSku } from "../../utils/skuUtils";
import {
  simularPrazosPedido,
  gerarMensagemWhatsAppSimulacao,
  ETAPAS_LABELS,
} from "../../utils/prazoSimulatorHelper";
import {
  formatDateToIso,
  normalizeToNoon,
  getBusinessDaysBetween,
} from "../../utils/formatters";
import { formatDiasUteis, formatDataComDiaSemana } from "../../utils/prazoEngine";

export default function PrazoSimulatorModal({
  isOpen,
  onClose,
  findCatalogItem: propFindCatalogItem,
  initialSku = "",
}) {
  const {
    prazosModelo = [],
    temposEtapaMap = {},
    prazosMap = {},
    feriados = [],
    feriadosSet = new Set(),
  } = useProductionContext() || {};

  const { catalog = [], findCatalogItem: hookFindCatalogItem } = useCatalog() || {};
  const findCatalog = propFindCatalogItem || hookFindCatalogItem;

  // Data de início da simulação (padrão: hoje em formato YYYY-MM-DD)
  const [dataInicioStr, setDataInicioStr] = useState(() => {
    return formatDateToIso(new Date()) || new Date().toISOString().split("T")[0];
  });

  // Itens na simulação atual
  const [simulatedItems, setSimulatedItems] = useState([]);

  // Input de busca no catálogo / SKU
  const [searchInput, setSearchInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedModelOverride, setSelectedModelOverride] = useState("");

  // Aba ativa de visualização no modal: "resumo" | "etapas"
  const [activeSubTab, setActiveSubTab] = useState("resumo");

  // Formato da mensagem para WhatsApp: "completo" | "prometido" | "estimado"
  const [modoWhatsApp, setModoWhatsApp] = useState("completo");

  // Estado do botão de cópia do WhatsApp
  const [copied, setCopied] = useState(false);

  const searchBoxRef = useRef(null);

  // Se receber initialSku ao abrir, adiciona automaticamente
  useEffect(() => {
    if (isOpen && initialSku && simulatedItems.length === 0) {
      handleAddItem(initialSku);
    }
  }, [isOpen, initialSku]);

  // Fecha dropdown se clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lista de modelos disponíveis para seleção manual
  const listaModelosDisponiveis = useMemo(() => {
    if (Array.isArray(prazosModelo) && prazosModelo.length > 0) {
      return prazosModelo.map((m) => m.modelo).filter(Boolean);
    }
    return Object.keys(prazosMap).filter((k) => k !== "Geral");
  }, [prazosModelo, prazosMap]);

  // Sugestões de busca no catálogo
  const catalogSuggestions = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    if (!term || term.length < 2) return [];

    const itemsList = Array.isArray(catalog) ? catalog : Object.values(catalog || {});
    return itemsList
      .filter((it) => {
        const sku = String(it.sku || it.SKU || "").toLowerCase();
        const name = String(it.name || it.nome || it.titulo || it.title || "").toLowerCase();
        const cat = String(it.category || it.categoria || "").toLowerCase();
        return sku.includes(term) || name.includes(term) || cat.includes(term);
      })
      .slice(0, 6);
  }, [searchInput, catalog]);

  // Adiciona item à simulação
  const handleAddItem = (skuInput, catalogData = null) => {
    const rawSku = String(skuInput || "").trim();
    if (!rawSku) return;

    const skuLimpo = cleanSku(rawSku);
    const catItem = catalogData || (findCatalog ? findCatalog(skuLimpo) : null);
    const modeloDetectado =
      selectedModelOverride || getModelFromSku(skuLimpo) || catItem?.modelo || "Geral";

    const newItem = {
      id: "sim_" + Date.now() + "_" + Math.random().toString(36).substr(2, 4),
      sku: skuLimpo,
      modelo: modeloDetectado,
      catalogItem: catItem,
    };

    setSimulatedItems((prev) => [...prev, newItem]);
    setSearchInput("");
    setShowDropdown(false);
    setSelectedModelOverride("");
  };

  // Remove item da simulação
  const handleRemoveItem = (id) => {
    setSimulatedItems((prev) => prev.filter((it) => it.id !== id));
  };

  // Atualiza o modelo de um item já adicionado
  const handleUpdateItemModel = (id, newModel) => {
    setSimulatedItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, modelo: newModel } : it))
    );
  };

  // Cálculo da simulação do pedido completo
  const simulacao = useMemo(() => {
    const dataInicio = normalizeToNoon(dataInicioStr) || normalizeToNoon(new Date());

    return simularPrazosPedido({
      itens: simulatedItems,
      dataInicio,
      prazosMap,
      temposEtapaMap,
    });
  }, [simulatedItems, dataInicioStr, prazosMap, temposEtapaMap]);

  // Feriados que caem no intervalo da simulação
  const feriadosNoPeriodo = useMemo(() => {
    if (!simulacao || simulacao.totalItens === 0) return [];
    const inicio = simulacao.dataInicio;
    const fim = simulacao.dataPrometidaPedido;
    if (!inicio || !fim) return [];

    const startIso = formatDateToIso(inicio);
    const endIso = formatDateToIso(fim);

    return (feriados || []).filter((f) => {
      if (f.ativo === false) return false;
      return f.data >= startIso && f.data <= endIso;
    });
  }, [simulacao, feriados]);

  // Mensagem para WhatsApp
  const mensagemWhatsApp = useMemo(() => {
    return gerarMensagemWhatsAppSimulacao({
      itensSimulados: simulacao.itensSimulados,
      prazoEstimadoPedido: simulacao.prazoEstimadoPedido,
      prazoPrometidoPedido: simulacao.prazoPrometidoPedido,
      dataEstimadaPedidoFormatada: simulacao.dataEstimadaPedidoFormatada,
      dataPrometidaPedidoFormatada: simulacao.dataPrometidaPedidoFormatada,
      dataInicioFormatada: simulacao.dataInicioFormatada,
      modo: modoWhatsApp,
    });
  }, [simulacao, modoWhatsApp]);

  // Copia mensagem para a área de transferência
  const handleCopiarMensagem = async () => {
    if (!mensagemWhatsApp) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(mensagemWhatsApp);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = mensagemWhatsApp;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Erro ao copiar para clipboard:", err);
      alert("Não foi possível copiar automaticamente. Selecione e copie o texto manualmente.");
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10005] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 md:p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col border border-slate-200 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ======================================================== */}
        {/* HEADER DO SIMULADOR                                      */}
        {/* ======================================================== */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-blue-950 text-white p-4 md:p-5 flex items-start justify-between shrink-0 shadow-md">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-blue-600/30 border border-blue-400/30 rounded-xl text-blue-300 shadow-inner">
                <Clock size={20} />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-black tracking-tight flex items-center gap-2">
                  <span>Simulador de Prazos de Venda</span>
                  <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-400/30 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                    Oficina
                  </span>
                </h2>
                <p className="text-xs text-slate-300">
                  Consulte a previsão de fabricação e projete a data de entrega antes de fechar o pedido
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
            title="Fechar simulador"
          >
            <X size={20} />
          </button>
        </div>

        {/* ======================================================== */}
        {/* CORPO DO SIMULADOR COM SCROLL                            */}
        {/* ======================================================== */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 custom-scrollbar text-xs bg-slate-50/50">
          {/* BARRA DE CONFIGURAÇÃO DE ENTRADA: DATA E BUSCA DE PRODUTO */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              {/* Data de Fechamento / Início */}
              <div className="md:col-span-4 space-y-1">
                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <Calendar size={13} className="text-blue-600" />
                  <span>Data Início / Pedido:</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dataInicioStr}
                    onChange={(e) => setDataInicioStr(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all cursor-pointer"
                  />
                </div>
              </div>

              {/* Busca no Catálogo ou SKU */}
              <div className="md:col-span-8 space-y-1" ref={searchBoxRef}>
                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <Search size={13} className="text-blue-600" />
                  <span>Adicionar Produto ou Digitar SKU:</span>
                </label>
                <div className="flex gap-2 relative">
                  <div className="relative flex-1">
                    <Search
                      size={15}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={searchInput}
                      onFocus={() => setShowDropdown(true)}
                      onChange={(e) => {
                        setSearchInput(e.target.value);
                        setShowDropdown(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && searchInput.trim()) {
                          handleAddItem(searchInput.trim());
                        }
                      }}
                      placeholder="Digite o SKU (ex: DIR-TRI-Z-16) ou busque pelo nome..."
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-medium text-slate-800 placeholder:text-slate-400"
                    />

                    {/* Dropdown de Autocomplete do Catálogo */}
                    {showDropdown && catalogSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto custom-scrollbar divide-y divide-slate-100">
                        {catalogSuggestions.map((catItem, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleAddItem(catItem.sku || catItem.SKU, catItem)}
                            className="p-2.5 hover:bg-blue-50/80 cursor-pointer flex items-center justify-between gap-3 transition-colors"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                                {catItem.image || catItem.imagem || catItem.foto ? (
                                  <img
                                    src={catItem.image || catItem.imagem || catItem.foto}
                                    alt="Foto"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <Gem size={14} className="text-slate-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold text-slate-800 truncate">
                                  {catItem.name || catItem.nome || catItem.titulo || catItem.sku}
                                </div>
                                <div className="text-[10px] font-mono text-slate-500 truncate">
                                  SKU: {catItem.sku || catItem.SKU}
                                </div>
                              </div>
                            </div>
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded shrink-0">
                              + Adicionar
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (searchInput.trim()) {
                        handleAddItem(searchInput.trim());
                      }
                    }}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer shrink-0"
                  >
                    <Plus size={15} />
                    <span className="hidden sm:inline">Adicionar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* LISTA DE ITENS NA SIMULAÇÃO */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Layers size={14} className="text-blue-600" />
                <span>Joias na Simulação ({simulatedItems.length})</span>
              </h3>
              {simulatedItems.length > 0 && (
                <button
                  onClick={() => setSimulatedItems([])}
                  className="text-[11px] text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                >
                  Limpar todos
                </button>
              )}
            </div>

            {simulatedItems.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto shadow-xs">
                  <Sparkles size={22} />
                </div>
                <p className="text-xs font-bold text-slate-700">
                  Nenhum produto adicionado à simulação ainda.
                </p>
                <p className="text-[11px] text-slate-400 max-w-md mx-auto">
                  Digite o SKU ou selecione joias no catálogo acima para calcular os prazos de produção e a previsão de entrega.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {simulacao.itensSimulados.map((it, idx) => {
                  const isCritico =
                    simulacao.totalItens > 1 &&
                    simulacao.itemCritico?.sku === it.sku &&
                    simulacao.itemCritico?.prazoPrometido === it.prazoPrometido;

                  return (
                    <div
                      key={it.sku + idx}
                      className={`p-3.5 bg-white border rounded-2xl transition-all shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-3 ${
                        isCritico
                          ? "border-blue-400/80 ring-1 ring-blue-400/20 bg-blue-50/20"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                          {it.imagem ? (
                            <img
                              src={it.imagem}
                              alt={it.titulo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Gem size={18} className="text-slate-400" />
                          )}
                        </div>

                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xs text-slate-800 truncate">
                              {it.sku}
                            </span>
                            {isCritico && (
                              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200 shrink-0">
                                Gargalo do Pedido
                              </span>
                            )}
                          </div>

                          <div className="text-[11px] text-slate-600 truncate font-medium">
                            {it.titulo}
                          </div>

                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            <span>Modelo:</span>
                            <select
                              value={it.modelo}
                              onChange={(e) =>
                                handleUpdateItemModel(simulatedItems[idx].id, e.target.value)
                              }
                              className="bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-700 outline-none cursor-pointer"
                            >
                              <option value={it.modelo}>{it.modelo}</option>
                              {listaModelosDisponiveis
                                .filter((m) => m !== it.modelo)
                                .map((m) => (
                                   <option key={m} value={m}>
                                     {m}
                                   </option>
                                 ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Métricas do Item: Estimativa da Fábrica vs Limite Comercial */}
                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-5 shrink-0 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-2 md:pt-0 border-slate-100">
                        <div className="text-right">
                          <div className="text-[10px] uppercase font-bold text-blue-600 flex items-center justify-end gap-1">
                            <Clock size={11} />
                            <span>Estimativa Fábrica</span>
                          </div>
                          <div className="text-xs font-black text-slate-800 font-mono">
                            ~{it.prazoEstimadoFormatado} d.ú.
                          </div>
                          <div className="text-[10px] font-bold text-blue-600 font-mono">
                            {it.dataEstimadaFormatada}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-[10px] uppercase font-bold text-slate-500 flex items-center justify-end gap-1">
                            <ShieldCheck size={11} className="text-slate-400" />
                            <span>Prazo Limite Venda</span>
                          </div>
                          <div className="text-xs font-bold text-slate-700 font-mono">
                            {it.prazoPrometidoFormatado} d.ú.
                          </div>
                          <div className="text-[10px] font-medium text-slate-500 font-mono">
                            {it.dataPrometidaFormatada}
                          </div>
                        </div>

                        <div className="text-right hidden sm:block">
                          <div className="text-[10px] uppercase font-bold text-emerald-600">
                            Margem / Folga
                          </div>
                          <div className="text-xs font-bold text-emerald-700 font-mono">
                            +{it.folgaEstimadaFormatada} d.ú.
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveItem(simulatedItems[idx].id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Remover joia da simulação"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ======================================================== */}
          {/* RESULTADO GLOBAL DA SIMULAÇÃO                            */}
          {/* ======================================================== */}
          {simulacao.totalItens > 0 && (
            <div className="space-y-4">
              {/* CARDS DE DESTAQUE: ESTIMATIVA DA FÁBRICA VS PRAZO LIMITE COMERCIAL */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* CARD 1: ESTIMATIVA REALISTA DA FÁBRICA */}
                <div className="md:col-span-7 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white rounded-2xl p-4 md:p-5 shadow-lg shadow-blue-500/20 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

                  <div className="relative z-10 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-white/20 text-white border border-white/20 tracking-wider flex items-center gap-1">
                        <Clock size={11} />
                        <span>Prazo Estimado de Fabricação</span>
                      </span>
                      <span className="text-[10px] bg-blue-400/30 text-blue-100 px-2 py-0.5 rounded-md font-mono font-bold">
                        Oficina Real
                      </span>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-black tracking-tight flex items-baseline gap-2">
                      <span>~{simulacao.prazoEstimadoPedidoFormatado}</span>
                      <span className="text-sm md:text-base font-normal text-blue-100">dias úteis de produção</span>
                    </h3>

                    <p className="text-xs text-blue-100 flex items-center gap-1.5 pt-1">
                      <CalendarDays size={14} className="text-blue-200 shrink-0" />
                      <span>Previsão de Conclusão na Fábrica:</span>
                      <strong className="text-white font-mono underline decoration-blue-300">
                        {simulacao.dataEstimadaPedidoFormatada}
                      </strong>
                    </p>
                  </div>

                  <div className="relative z-10 mt-3 pt-3 border-t border-white/15 flex items-center justify-between text-[11px] text-blue-100">
                    <span>Gargalo da linha de bancada:</span>
                    <strong className="font-mono text-white">
                      {simulacao.itemCriticoEstimado?.modelo || simulacao.itemCritico?.modelo}
                    </strong>
                  </div>
                </div>

                {/* CARD 2: PRAZO LIMITE COMERCIAL (COM MARGEM DE SEGURANÇA) */}
                <div className="md:col-span-5 bg-gradient-to-br from-slate-900 to-slate-850 text-white rounded-2xl p-4 md:p-5 shadow-md border border-slate-700/60 relative overflow-hidden flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-200 border border-slate-700 tracking-wider flex items-center gap-1">
                        <ShieldCheck size={11} className="text-emerald-400" />
                        <span>Prazo Máximo Prometido</span>
                      </span>
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-md font-mono font-bold">
                        Venda Segura
                      </span>
                    </div>

                    <h4 className="text-2xl md:text-3xl font-black tracking-tight font-mono text-slate-100 flex items-baseline gap-2">
                      <span>{simulacao.prazoPrometidoPedidoFormatado}</span>
                      <span className="text-sm font-normal text-slate-300">dias úteis</span>
                    </h4>

                    <p className="text-xs text-slate-300 flex items-center gap-1.5 pt-1">
                      <Calendar size={13} className="text-slate-400 shrink-0" />
                      <span>Data Limite ao Cliente:</span>
                      <strong className="text-white font-mono">{simulacao.dataPrometidaPedidoFormatada}</strong>
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-700/80 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400">Margem de segurança:</span>
                    <span className="font-bold text-emerald-400 font-mono">
                      +{simulacao.folgaPedidoFormatada} d.ú. de folga
                    </span>
                  </div>
                </div>
              </div>

              {/* ALERTA DE FERIADOS CONSIDERADOS (SE HOUVER) */}
              {feriadosNoPeriodo.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900 flex items-start gap-2.5">
                  <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <div className="font-bold text-xs">
                      {feriadosNoPeriodo.length}{" "}
                      {feriadosNoPeriodo.length === 1
                        ? "feriado oficial considerado no cálculo:"
                        : "feriados oficiais considerados no cálculo:"}
                    </div>
                    <div className="text-[11px] text-amber-800">
                      {feriadosNoPeriodo.map((f, i) => (
                        <span key={f.id || i} className="inline-block mr-2 font-medium">
                          • {formatDataComDiaSemana(f.data)}: {f.descricao}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ABAS INTERNAS: MENSAGEM WHATSAPP OU CRONOGRAMA DE ETAPAS */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                <div className="border-b border-slate-200 bg-slate-50/60 p-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveSubTab("resumo")}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activeSubTab === "resumo"
                        ? "bg-white text-blue-600 shadow-xs border border-slate-200"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Share2 size={13} />
                    <span>Mensagem para WhatsApp</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveSubTab("etapas")}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activeSubTab === "etapas"
                        ? "bg-white text-blue-600 shadow-xs border border-slate-200"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Clock size={13} />
                    <span>Cronograma de Etapas da Fábrica</span>
                  </button>
                </div>

                <div className="p-4 md:p-5">
                  {/* ABA 1: MENSAGEM WHATSAPP PRONTA */}
                  {activeSubTab === "resumo" && (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] font-bold text-slate-600">Formato:</span>
                          <div className="inline-flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-bold">
                            <button
                              type="button"
                              onClick={() => setModoWhatsApp("completo")}
                              className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                                modoWhatsApp === "completo"
                                  ? "bg-white text-blue-600 shadow-xs"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              Completo (Estimado + Máximo)
                            </button>
                            <button
                              type="button"
                              onClick={() => setModoWhatsApp("prometido")}
                              className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                                modoWhatsApp === "prometido"
                                  ? "bg-white text-blue-600 shadow-xs"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              Apenas Limite Máximo
                            </button>
                            <button
                              type="button"
                              onClick={() => setModoWhatsApp("estimado")}
                              className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                                modoWhatsApp === "estimado"
                                  ? "bg-white text-blue-600 shadow-xs"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              Apenas Estimativa Fábrica
                            </button>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleCopiarMensagem}
                          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                            copied
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-800 hover:bg-slate-900 text-white"
                          }`}
                        >
                          {copied ? (
                            <>
                              <Check size={14} />
                              <span>Copiado com Sucesso!</span>
                            </>
                          ) : (
                            <>
                              <Copy size={14} />
                              <span>Copiar Mensagem</span>
                            </>
                          )}
                        </button>
                      </div>

                      <div className="relative">
                        <textarea
                          readOnly
                          rows={10}
                          value={mensagemWhatsApp}
                          className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 outline-none leading-relaxed select-all"
                        />
                      </div>
                    </div>
                  )}

                  {/* ABA 2: CRONOGRAMA DE ETAPAS DA FÁBRICA */}
                  {activeSubTab === "etapas" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500">
                        Projeção temporal de quando o item com maior tempo fabril (
                        <strong>{simulacao.itemCriticoEstimado?.modelo || simulacao.itemCritico?.modelo}</strong>) deverá concluir cada fase da bancada:
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
                        {(simulacao.itemCriticoEstimado || simulacao.itemCritico)?.cronogramaEtapas.map((etp, idx) => (
                          <div
                            key={etp.etapa}
                            className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-xs"
                          >
                            <div className="flex items-center justify-between text-slate-400 text-[10px] font-bold">
                              <span>Fase #{idx + 1}</span>
                              <span className="font-mono">{etp.duracaoDiasUteis} d.ú.</span>
                            </div>
                            <div className="font-bold text-slate-800 text-[11px] truncate">
                              {etp.label}
                            </div>
                            <div className="text-[10px] text-blue-600 font-mono font-bold flex items-center gap-1">
                              <Calendar size={10} />
                              <span>{etp.dataConclusaoFormatada}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>


        {/* ======================================================== */}
        {/* RODAPÉ DO MODAL                                          */}
        {/* ======================================================== */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
          <div className="text-[11px] text-slate-500 font-medium">
            {simulacao.totalItens === 0
              ? "Adicione joias para ver a previsão"
              : `${simulacao.totalItens} ${
                  simulacao.totalItens === 1 ? "joia simulada" : "joias simuladas"
                }`}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              Fechar
            </button>

            {simulacao.totalItens > 0 && (
              <button
                type="button"
                onClick={handleCopiarMensagem}
                className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                  copied
                    ? "bg-emerald-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {copied ? <Check size={14} /> : <Share2 size={14} />}
                <span>{copied ? "Copiado!" : "Copiar WhatsApp"}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
